import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from './cn';

/**
 * DESIGN.md › answer-option (Figma "toggle chip"): 52px outlined option with a hard 1px
 * drop shadow — math answers, the "What would you like to…?" chooser, and the timer sheet's
 * Pause/Reset. Selection turns the fill teal-20; `muted` greys it out (a math answer that
 * wasn't right). The label always names the state.
 */
export function AnswerOption({
  selected = false,
  muted = false,
  icon,
  className,
  children,
  type = 'button',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  selected?: boolean;
  muted?: boolean;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <button
      type={type}
      aria-pressed={selected}
      {...props}
      className={cn(
        'flex h-answer-option w-full cursor-pointer items-center justify-center gap-internal-md rounded-sm border px-content-sm font-brand text-title-lg transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-highlight disabled:cursor-default',
        selected
          ? 'border-border-highlight bg-control-selected text-on-control-selected shadow-[0_1px_0_var(--color-border-highlight)]'
          : muted
            ? 'border-border-strong bg-surface-tertiary text-on-surface-weak shadow-[0_1px_0_var(--color-border-strong)]'
            : 'border-border-highlight bg-control-unselected text-on-action-secondary shadow-[0_1px_0_var(--color-border-highlight)] hover:bg-action-secondary-focus',
        className,
      )}
    >
      {icon}
      {children}
    </button>
  );
}
